import {
  Badge,
  Button,
  FlexBox,
  Page,
  Table,
  Tag,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isFetchBaseQueryError } from 'lib-shared-auth';
import capitalize from 'lodash/capitalize';
import { useMemo } from 'react';
import { useInterval } from 'react-use';

import {
  KnowledgeBase,
  knowledgeBasesApi,
  KnowledgeBaseType,
} from 'apis/knoledgeBases';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { KnowledgeBaseActionsMenu } from './components/knowledgeBaseActionMenu/knowledgeBaseActionMenu';
import { KnowledgeBaseModal } from './components/knowledgeBaseModal/knowledgeBaseModal';
import styles from './knowledgeBase.module.scss';

export const KnowledgeBaseView = (): JSX.Element => {
  const organizationId = useAppSelector(
    (state) => state.settings.organization.organization.id,
  );

  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const flags = useFeatureFlag(['knowledge-access', 'website-data-source']);
  const knowledgeSource: KnowledgeBaseType[] = useMemo(
    () =>
      [
        'elevio',
        ...(flags.knowledgeAccess ? ['dixa_knowledge'] : []),
        ...(flags.websiteDataSource ? ['website'] : []),
      ] as KnowledgeBaseType[],
    [flags.knowledgeAccess, flags.websiteDataSource],
  );

  const { knowledgeBases, isLoading, isSuccess, isError, refetch } =
    knowledgeBasesApi.useReadKnowledgeBasesQuery(
      { organizationId: organizationId, source: knowledgeSource },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            knowledgeBases: data?.data.knowledgeBases
              ? [...data.data.knowledgeBases].reverse()
              : [],
          };
        },
      },
    );
  const [deleteKnowledge] = knowledgeBasesApi.useDeleteKnowledgeBaseMutation();

  useInterval(
    () => refetch(),
    knowledgeBases.some((kb) => kb.state !== 'ready') ? 20000 : null,
  );

  const handleCreateKnowledgeBase = async () => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:knowledgeBaseModal',
          arg: { organizationId },
        },
      }),
    );
  };

  const handleEditKnowledgeBase = async (knowledgeBase: KnowledgeBase) => {
    if (knowledgeBase.source === 'website') {
      navigate({
        path: '/settings/chatbots/data-source/:id',
        params: { path: { id: knowledgeBase.id } },
      });
    } else {
      await dispatch(
        appActions.showModal({
          modal: {
            type: 'settings:knowledgeBaseModal',
            arg: {
              knowledgeBase,
              organizationId,
            },
          },
        }),
      );
    }
  };

  const handleDeleteKnowledgeBase = async (knowledgeBase: KnowledgeBase) => {
    const name = knowledgeBase.name || '';

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${name}"`,
            description: `Are you sure you want to permanently delete the "${name}"? This action cannot be undone.`,
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        await deleteKnowledge({ id: knowledgeBase.id }).unwrap();
        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error: any) {
        if (isFetchBaseQueryError(error) && error.status === 409) {
          notify(
            {
              title: `Knowledge base "${name}" in use. Deletion not allowed.`,
              icon: 'exclamation',
              className: 'warning',
              time: 5000,
            },
            NotificationType.Error,
          );
        } else {
          notify(
            {
              title: `Error while deleting "${name}".`,
              icon: 'exclamation',
              className: 'danger',
              time: 5000,
            },
            NotificationType.Error,
          );
        }
      }
    }
  };

  return (
    <>
      <Page.Section
        title="Data sources"
        titleMeta={
          knowledgeBases.length > 0 && (
            <Badge size="small" variant="secondary">
              {knowledgeBases.length}
            </Badge>
          )
        }
        controls={
          <Button variant="secondary" onClick={handleCreateKnowledgeBase}>
            Add data source
          </Button>
        }
        className={styles.container}
      >
        <Table
          stickyHeader
          loading={isLoading}
          loadingFailed={isError}
          emptyState={
            isSuccess && knowledgeBases.length === 0
              ? {
                  title: 'Add data source',
                  description:
                    'Add your first data source to be able to use a Mim chatbot.',
                  buttonProps: {
                    onClick: handleCreateKnowledgeBase,
                    children: 'Add data source',
                    variant: 'secondary',
                  },
                }
              : undefined
          }
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th columnId="name">Name</Table.Th>
              <Table.Th columnId="source">Source</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {knowledgeBases?.map((kb, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <FlexBox gap="100">
                    <Tooltip content={capitalize(kb.state)} key={kb.id}>
                      <Tag
                        color={
                          kb.state === 'ready'
                            ? colors.green[400]
                            : colors.orange[400]
                        }
                      >
                        {kb.name}
                      </Tag>
                    </Tooltip>
                  </FlexBox>
                </Table.Td>
                <Table.Td>
                  {capitalize(kb.source.replace(/_/g, ' ') ?? '')}
                </Table.Td>
                <Table.Td align="right">
                  {kb.state === 'ready' && (
                    <KnowledgeBaseActionsMenu
                      onEditCredentials={() => handleEditKnowledgeBase(kb)}
                      onDeleteKnowledgeBase={() =>
                        handleDeleteKnowledgeBase(kb)
                      }
                    />
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Page.Section>
      <KnowledgeBaseModal />
    </>
  );
};
